import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@react-forked/dnd';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { userAtom } from '@/state/atom/userAtom';
import { closeTabId, multiPanelAtom, panelInitDataAtom } from '@/state/atom/panelAtom';

import { Button } from '@material-ui/core';
import './PanelManagement.css';

import Utils from '@/common/utils/utils';
import IconPreview from '@/component/common/layout/gNav/icon/IconPreview';
import OverflowTooltipContent from '@/component/common/OverflowTooltipContent';
import { closePanelTabSelector } from '@/state/selector/panelSelector';
import { updatePanelInitData } from '@/component/multiPanel/panel/handler/PanelHandler';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { buildShareLinkBusinessApp, handleSubmitManagementPanel, initManagementPanels } from '../handler/PanelManagementHandler';
import LoadingPanel from '@/component/common/LoadingPanel';
import MuiTooltip from '@/component/common/MuiTooltip';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import tr from '@/common/utils/tr';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

function PanelManagement(props) {
    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    const [managementPanel, setManagementPanel] = useState({
        basePanel: [],
        businessApp: []
    });
    const [isEdited, setIsEdited] = useState(false);
    const [isDisplayConfirmCancel, setIsDisplayConfirmCancel] = useState(false);
    const [isShowPopUpCloseTab, setIsShowPopUpCloseTab] = useState(false);

    const corporationDetail = useRecoilValue(corporationAtom);
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);
    const userDetail = useRecoilValue(userAtom);

    const panelList = useRecoilValue(multiPanelAtom);
    const [panelInitData, setPanelInitData] = useRecoilState(panelInitDataAtom);
    const [closeTabIdState, setCloseTabIdState] = useRecoilState(closeTabId);
    const closePanelTab = useSetRecoilState(closePanelTabSelector);

    const [isLoading, setIsLoading] = useState(false);
    const [isShowPopUpReload, setIsShowPopUpReload] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    
    useEffect(() => {
        if (!corporationDetail || !projectInuseDetail) return;
        setIsLoading(true)
        initManagementPanels(corporationDetail, projectInuseDetail, setManagementPanel)
            .finally(() => { setIsLoading(false) })
    }, [corporationDetail, projectInuseDetail]);

    useEffect(() => {
        if (props.tabId === closeTabIdState) {
            if (isEdited) {
                setIsShowPopUpCloseTab(true);
            } else {
                let currentPanelInfo = Utils.getPanelInfo(props.panelId, panelList);
                if (!currentPanelInfo || Utils.isEmptyObject(currentPanelInfo)) return;
                closePanelTab({
                    event: null,
                    panelType: currentPanelInfo.type,
                    panelId: props.panelId,
                    tabId: props.tabId
                });
            }
            setCloseTabIdState(0);
        }
    }, [closeTabIdState]);

    useEffect(() => {
        updatePanelInitData(
            props.panelId,
            props.tabId,
            panelInitData,
            setPanelInitData,
            'isEditedData',
            isEdited
        );
    }, [isEdited]);

    const onSubmitManagementPanel = () => {
        setIsLoading(true);
        setIsEdited(false);
        return handleSubmitManagementPanel(
            managementPanel,
            {
                corporationDetail,
                projectInuseDetail,
                userDetail,
                enqueueSnackbar,
                setIsLoading,
                setIsEdited,
                setIsShowPopUpReload
            }
        )
    }

    return (
        <div className="panel-management-wrapper">
            {/* Header */}
            <div className="panel-management-header">
                <div className="panel-management-btns">
                    <Button
                        className="btn-hg32 btn-light panel-management-btn"
                        color="primary"
                        variant="contained"
                        disabled={!isEdited}
                        onClick={() => {
                            if (isEdited) setIsDisplayConfirmCancel(true);
                        }}
                    >
                        {trI18n('CM-BUTTON-CANCEL')}
                    </Button>

                    <Button
                        className="btn-hg32 btn-primary panel-management-btn"
                        color="primary"
                        variant="contained"
                        disabled={!isEdited}
                        onClick={onSubmitManagementPanel}
                    >
                        {trI18n('CM-BUTTON-SAVE')}
                    </Button>
                </div>
            </div>

            <LoadingPanel isLoading={isLoading} />

            {/* Body */}
            <div className="panel-management-body">
                <PanelManageMentSetting
                    groupKey={'basePanel'}
                    listApp={managementPanel?.basePanel || []}
                    setIsEdited={setIsEdited}
                    setManagementPanel={setManagementPanel}
                />
                <PanelManageMentSetting
                    groupKey={'businessApp'}
                    listApp={managementPanel?.businessApp || []}
                    setIsEdited={setIsEdited}
                    setManagementPanel={setManagementPanel}
                />
            </div>

            {/* Confirm */}
            <ConfirmDialog
                isOpen={isDisplayConfirmCancel || isShowPopUpCloseTab}
                msgConfirm={trI18n('MSG-CM-CONFIRM-EXIT-PAGE-WITHOUT-SAVING')}
                okCallBack={async () => {
                    if (isDisplayConfirmCancel) setIsDisplayConfirmCancel(false)
                    if (isShowPopUpCloseTab) setIsShowPopUpCloseTab(false)
                    await onSubmitManagementPanel()
                }}
                closeCallBack={() => {
                    let currentPanelInfo = Utils.getPanelInfo(props.panelId, panelList);
                    if (!currentPanelInfo || Utils.isEmptyObject(currentPanelInfo)) return;
                    setIsEdited(false);
                    closePanelTab({
                        event: null,
                        panelType: currentPanelInfo.type,
                        panelId: props.panelId,
                        tabId: props.tabId
                    });
                    setCloseTabIdState(0);
                    if (isDisplayConfirmCancel) setIsDisplayConfirmCancel(false)
                    if (isShowPopUpCloseTab) setIsShowPopUpCloseTab(false)
                }}
                cancelCallBack={() => {
                    if (isDisplayConfirmCancel) setIsDisplayConfirmCancel(false)
                    if (isShowPopUpCloseTab) setIsShowPopUpCloseTab(false)
                }}
                textSubmit={trI18n('COMBTN002')}
                textClose={trI18n('COMBTN007')}
                isShowClassnameButton={false}
            />
            <ConfirmDialog
                isOpen={isShowPopUpReload}
                msgConfirm={trI18n('COMMSG030')}
                okCallBack={() => window.location.reload()}
            />
        </div>
    );
}
PanelManagement.propTypes = {
    tabId: PropTypes.string,
    panelId: PropTypes.string
};

const PanelManageMentSetting = ({ groupKey, listApp, setIsEdited, setManagementPanel }) => {
    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    const [apps, setApps] = useState(listApp || []);
    const [isShowApps, setIsShowApps] = useState(true);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (JSON.stringify(listApp) !== JSON.stringify(apps)) {
            setApps(listApp || []);
        }
    }, [listApp]);

    useEffect(() => {
        setManagementPanel(prev => {
            return {
                ...prev,
                [groupKey]: [...apps]
            };
        });
    }, [apps]);

    const handleDragEnd = result => {
        if (!result.destination) return;
        setApps(prev => {
            const reordered = [...prev];
            const [moved] = reordered.splice(result.source.index, 1);
            reordered.splice(result.destination.index, 0, moved);

            return reordered.map((app, index) => ({
                ...app,
                order: index
            }));
        });
        setIsEdited(true);
    };

    const handleDisplayOnSideBar = (id, checked) => {
        const LIMIT_DISPLAY_SIDEBAR = 10;
        setApps(prev => {
            const updated = prev.map(app =>
                app.id === id
                    ? { ...app, active: checked, checkedAt: checked ? Date.now().toString() : null }
                    : app
            );

            if (groupKey === 'businessApp') {
                const listAppOnDisplayCount = updated.filter(app => app.active).length;
                if (listAppOnDisplayCount > LIMIT_DISPLAY_SIDEBAR) {
                    enqueueSnackbar(buildSnackbarMessage('MSG_VALIDATE_LIMIT_DISPLAY_APP'), {
                        variant: 'error',
                        autoHideDuration: 3000
                    })
                    return prev;
                }
            }

            setIsEdited(true);
            return updated;
        });
    };


    return (
        <div style={{ width: '100%' }}>
            <button
                className={`accordion-head`}
                onClick={() => {
                    setIsShowApps(prev => !prev)
                }}
                type="button"
            >
                <span className="text">
                    {trI18n(groupKey === 'basePanel' ? 'BASE_PANEL' : 'BUSINESS_APP')}
                </span>
                {isShowApps ? (
                    <img src="/static/media/icon-minus-accordion.24e40269.svg" alt="minus" />
                ) : (
                    <img src="/static/media/icon-plus-accordion.d9a73563.svg" alt="plus" />
                )}
            </button>

            {/* Render list App, handle Drag/Drop */}
            {!Utils.isEmptyArray(apps) && (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId={`droppable-apps-${groupKey}`}>
                        {provided => (
                            <div
                                className={`app-list ${!isShowApps ? 'display-none' : ''}`}
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                            >
                                {apps.map((app, index) => (
                                    <Draggable key={app.id} draggableId={String(app.id)} index={index} isDragDisabled={groupKey === 'basePanel'}>
                                        {(provider, snapshot) => {
                                            if (snapshot.isDragging) {
                                                provider.draggableProps.style.left =
                                                    'auto!important';
                                            }

                                            return (
                                                <div
                                                    ref={provider.innerRef}
                                                    {...provider.draggableProps}
                                                    {...provider.dragHandleProps}
                                                    className={`app-item-wrapper ${
                                                        snapshot.isDragging ? 'dragging' : ''
                                                    }`}
                                                >
                                                    <AppItem
                                                        app={app}
                                                        handleDisplayOnSideBar={handleDisplayOnSideBar}
                                                    />
                                                </div>
                                            )
                                        }}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}
        </div>
    );
};
PanelManageMentSetting.propTypes = {
    groupKey: PropTypes.string,
    listApp: PropTypes.array,
    setIsEdited: PropTypes.func,
    setManagementPanel: PropTypes.func
};

const AppItem = ({app, handleDisplayOnSideBar}) => {
    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    const appName = tr(app?.name) || '';
    const appNameLength = app?.name?.length || 0;

    return (
        <div className='app-item-inner'>
            <label htmlFor={`using-check-app-${app.id}`} className="using-check-app-wrapper">
                <input
                    className="using-check-app-item"
                    type="checkbox"
                    checked={app.active || false}
                    id={`using-check-app-${app.id}`}
                    onChange={e =>
                        handleDisplayOnSideBar( app.id, e.target.checked)
                    }
                />
            </label>
            <OverflowTooltipContent
                className="app-item-label"
                text={appName}
                tooltipMaxWidth={appNameLength > 1000 ? appNameLength / 3 : null}
            />
            {app?.affiliationProject && (
                <OverflowTooltipContent text={app?.createdProjectName} />
            )}
            {app?.appSrc && (
                <MuiTooltip title={trI18n('OPEN_ON_NEXT_TAB')}>
                    <button
                        className="btn-reset-default app-item-open-view-app-wrapper"
                        onClick={() => {
                            if (!app?.appSrc) return;
                            const shareLink = buildShareLinkBusinessApp(app?.appSrc)
                            window.open(shareLink, '_blank')
                        }}
                    >
                        <IconPreview />
                    </button>
                </MuiTooltip>
            )}
        </div>
    )
}

export default PanelManagement;
